import { useCallback, useEffect, useState } from "react";

import { useUser } from "MVVM/Hooks/useUser";

const { REACT_APP_STRIPE_ENDPOINT } = process.env;

type TTrialCardStatus = {
  trialing: boolean;
  hasCard: boolean;
  trialEnd: number | null;
  extended: boolean;
  reactivationEligible: boolean;
  reactivationDeadline: number | null;
};

/**
 * Whether the signed-in trial user has a card attached to their trialing
 * subscription — and, after expiry, whether the 7 bonus days are still
 * claimable (ALL-1838). Answered by the payment endpoint from live Stripe
 * data — default_payment_method, the extension stamp, and the expired sub's
 * timing aren't on the user record, and a localStorage marker wouldn't
 * survive a second device.
 */
export const useTrialCardStatus = () => {
  const { allstarUser } = useUser();
  const userGroup = allstarUser?.user?.userGroup;
  const isTrialPeriod = !!userGroup?.isTrialPeriod;
  // Post-expiry claim candidates: had a trial, currently below the trial tier.
  // Mirrors the server's cheap gate so users with no trial history never make
  // the request at all.
  const mayReactivate =
    !!userGroup?.hasUsedPlatinumTrial &&
    userGroup?.type !== "CREATOR_PLAT" &&
    userGroup?.type !== "INTERNAL";
  const shouldFetch = isTrialPeriod || mayReactivate;
  const token = allstarUser?.token;

  const [status, setStatus] = useState<TTrialCardStatus | null>(null);
  const [loading, setLoading] = useState(shouldFetch);

  const refetch = useCallback(async () => {
    if (!shouldFetch || !token) {
      setStatus(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(
        `${REACT_APP_STRIPE_ENDPOINT}/stripe/v1/request?trialCardStatus=1`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const data = await res.json();
      if (res.ok) {
        setStatus({
          trialing: !!data.trialing,
          hasCard: !!data.hasCard,
          trialEnd: data.trialEnd ?? null,
          extended: !!data.extended,
          reactivationEligible: !!data.reactivationEligible,
          reactivationDeadline: data.reactivationDeadline ?? null,
        });
      }
    } catch {
      // Leave status null — surfaces gated on it simply don't render.
    } finally {
      setLoading(false);
    }
  }, [shouldFetch, token]);

  useEffect(() => {
    refetch();
  }, [refetch]);

  return { status, loading, refetch };
};
